/**
 * BlogSubscribeWidget — opt-in card on the blog page that lets readers
 * subscribe to email notifications for new posts. Supports category
 * multi-select so readers only get pinged about topics they care about.
 *
 * Wire: POST /api/blog/subscribe (double opt-in confirmation flow).
 * Categories are passed as a slug list — empty list means "all posts".
 *
 * UX choices:
 *   - Collapsed by default (a single inline trigger row) so the card
 *     doesn't dominate the layout. Expands inline when a reader taps
 *     it — no modal stacking, no router navigation, no friction.
 *   - Category chips are click-to-toggle (no <select multiple>) so
 *     touch users don't have to wrestle with the OS multi-select UI.
 *   - Success state replaces the form rather than appending below it,
 *     so the reader gets a clean "you're done" moment with the next
 *     concrete action (check inbox) front-and-centre.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Mail, ChevronDown, ChevronUp, CheckCircle2, Loader2 } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

const BlogSubscribeWidget = ({ categories = [], isSpanish = false }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState(new Set());
  // "instant" = email per matching publish, "weekly" = Sunday digest.
  // Default to instant so existing-link sharers keep their behaviour.
  const [cadence, setCadence] = useState('instant');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Pick the subset of categories worth showing as chips — drop any
  // that have zero published posts so we never invite a subscription
  // to an empty bucket.
  const showCats = (categories || []).filter((c) => (c.published_count ?? c.count ?? 0) > 0);

  const togglePick = (slug) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const submit = async () => {
    const cleanEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      return toast.error(isSpanish ? 'Ingresa un email válido.' : 'Please enter a valid email.');
    }
    setSubmitting(true);
    const { data, error } = await apiFetch('/api/blog/subscribe', {
      method: 'POST',
      body: { email: cleanEmail, categories: Array.from(picked), cadence },
    });
    setSubmitting(false);
    if (error) return toast.error(error);
    setDone(true);
    toast.success(data?.message || (isSpanish ? 'Revisa tu email para confirmar.' : 'Check your email to confirm.'));
  };

  if (done) {
    return (
      <Card
        className="border-[#00A896]/40 bg-gradient-to-br from-[#00E5CC]/10 to-[#00A896]/10"
        data-testid="blog-subscribe-done"
      >
        <CardContent className="p-4 sm:p-5 text-center">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-[#00A896]" />
          <p className="font-semibold text-base mb-1">
            {isSpanish ? '¡Casi listo!' : 'Almost there!'}
          </p>
          <p className="text-sm text-muted-foreground">
            {isSpanish
              ? 'Te enviamos un email de confirmación. Haz clic en el enlace para empezar a recibir avisos.'
              : 'We sent you a confirmation email — click the link to start receiving notifications.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-[#00A896]/30 bg-gradient-to-br from-[#00E5CC]/5 to-[#00A896]/5"
      data-testid="blog-subscribe-widget"
    >
      <CardContent className="p-4 sm:p-5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-2 text-left"
          aria-expanded={open}
          data-testid="blog-subscribe-toggle"
        >
          <Mail className="h-5 w-5 text-[#00A896] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm sm:text-base">
              {isSpanish
                ? 'Recibe un email cuando publiquemos posts nuevos'
                : 'Get an email when we publish in [your topics]'}
            </p>
            <p className="text-xs text-muted-foreground hidden sm:block">
              {isSpanish
                ? 'Elige categorías y te avisaremos solo de lo que te interesa.'
                : 'Pick categories and we\'ll only ping you about what you care about.'}
            </p>
          </div>
          {open ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
        </button>

        {open && (
          <div className="mt-4 space-y-3">
            <Input
              type="email"
              inputMode="email"
              placeholder={isSpanish ? 'tu@email.com' : 'you@example.com'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              data-testid="blog-subscribe-email-input"
              disabled={submitting}
            />

            {showCats.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">
                  {isSpanish
                    ? 'Selecciona las categorías que te interesan (o no elijas ninguna para recibir todo):'
                    : 'Pick the categories you want (or leave empty to get everything):'}
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="blog-subscribe-cat-chips">
                  {showCats.map((c) => {
                    const slug = c.slug || c.name;
                    const label = c.label || c.name;
                    const isPicked = picked.has(slug);
                    return (
                      <button
                        key={slug}
                        type="button"
                        onClick={() => togglePick(slug)}
                        data-testid={`blog-subscribe-cat-${slug}`}
                        aria-pressed={isPicked}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition border ${
                          isPicked
                            ? 'bg-[#00A896] text-white border-[#00A896]'
                            : 'bg-background hover:bg-muted text-foreground border-muted-foreground/20'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {picked.size > 0 && (
                  <div className="text-[10px] text-muted-foreground mt-1.5 flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px]">{picked.size}</Badge>
                    <span>{isSpanish ? 'categorías seleccionadas' : 'categories selected'}</span>
                  </div>
                )}
              </div>
            )}

            {/* Cadence toggle — instant per-post OR weekly digest. */}
            <div className="space-y-1.5" data-testid="blog-subscribe-cadence">
              <p className="text-xs font-medium">
                {isSpanish ? '¿Con qué frecuencia?' : 'How often?'}
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => setCadence('instant')}
                  data-testid="blog-subscribe-cadence-instant"
                  aria-pressed={cadence === 'instant'}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition border text-left ${
                    cadence === 'instant'
                      ? 'bg-[#00A896] text-white border-[#00A896]'
                      : 'bg-background hover:bg-muted text-foreground border-muted-foreground/20'
                  }`}
                >
                  <div className="font-semibold">
                    {isSpanish ? 'Al instante' : 'Per post'}
                  </div>
                  <div className={`text-[10px] ${cadence === 'instant' ? 'text-white/85' : 'text-muted-foreground'}`}>
                    {isSpanish ? 'Email cuando publicamos' : 'Email when we publish'}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setCadence('weekly')}
                  data-testid="blog-subscribe-cadence-weekly"
                  aria-pressed={cadence === 'weekly'}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition border text-left ${
                    cadence === 'weekly'
                      ? 'bg-[#00A896] text-white border-[#00A896]'
                      : 'bg-background hover:bg-muted text-foreground border-muted-foreground/20'
                  }`}
                >
                  <div className="font-semibold">
                    {isSpanish ? 'Resumen semanal' : 'Weekly digest'}
                  </div>
                  <div className={`text-[10px] ${cadence === 'weekly' ? 'text-white/85' : 'text-muted-foreground'}`}>
                    {isSpanish ? 'Un email los domingos' : 'One email on Sundays'}
                  </div>
                </button>
              </div>
            </div>

            <Button
              onClick={submit}
              disabled={submitting || !email.trim()}
              className="w-full bg-[#00A896] hover:bg-[#008f81] text-white"
              data-testid="blog-subscribe-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {isSpanish ? 'Enviando…' : 'Sending…'}</>
              ) : (
                <><Mail className="h-4 w-4 mr-2" /> {isSpanish ? 'Suscribirme' : 'Subscribe'}</>
              )}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              {isSpanish
                ? 'Doble opt-in. Te enviaremos un email de confirmación.'
                : 'Double opt-in. We\'ll send a confirmation email — one click and you\'re done.'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BlogSubscribeWidget;
